/**
 * "Za kada ti je potreban poklon?" and the delivery promise, inside the
 * personalizer's last step (docs/product-page-plan-20261031.md §4–5).
 *
 * The connector ships its calendar rules as `window.personalizyDeliveryConfig`
 * (class-personalizy-delivery.php `rules()`): the same rules its own calendar
 * and the server's add-to-cart check use. This module reads them without a DOM:
 * what today is in the shop's time zone, which days can be picked, a month to
 * draw. The server stays the authority (`date_error`).
 *
 * The promise comes from the "Delivery ETA" plugin's two public, read-only
 * AJAX actions (no nonce: product pages are cached). Only their `eta_data` is
 * read — never their HTML, which is styled for the page and not for the
 * widget's sheet — and turned into the card's words here, so a plugin update
 * changes this file only.
 */

export interface DeliveryRules {
  timeZone: string;
  /** 0 = Sunday … 6 = Saturday: days nothing is delivered. */
  weekdays: number[];
  /** ISO days that are blocked (holidays included). */
  dates: string[];
  /** ISO [from, to] blocked periods. */
  ranges: Array<[string, string]>;
  /** "MM-DD" blocked every year. */
  yearly: string[];
  /** The earliest pickable day is today + minDays … */
  minDays: number;
  /** … one more after this time ("HH:MM", '' for none). */
  cutoff: string;
  /** Latest pickable day: today + maxDays (0 = no limit). */
  maxDays: number;
  required: boolean;
}

export interface DeliveryConfig extends DeliveryRules {
  productId?: number;
  /** The ETA plugin is active. */
  eta?: boolean;
  ajaxUrl?: string;
}

export const DELIVERY_TEXTS = {
  label: 'Za kada ti je potreban poklon?',
  asap: 'Što pre',
  asapNote: 'Šaljemo prvim slobodnim danom.',
  pick: 'Izaberi datum',
  change: 'Promeni datum',
  clear: 'Bez datuma',
  prevMonth: 'Prethodni mesec',
  nextMonth: 'Sledeći mesec',
  required: 'Izaberi datum do kada ti je potreban poklon.',
  invalid: 'Taj datum ne može da se izabere. Izaberi drugi dan.',
  ordered: 'Poručeno',
  shipped: 'Poslato',
  arrives: 'Stiže',
  today: 'danas',
  pickSafe: (day: string): string => `Izaberi ${day}`,
};

const MONTHS = ['januar', 'februar', 'mart', 'april', 'maj', 'jun', 'jul', 'avgust', 'septembar', 'oktobar', 'novembar', 'decembar'];
const MONTHS_GEN = ['januara', 'februara', 'marta', 'aprila', 'maja', 'juna', 'jula', 'avgusta', 'septembra', 'oktobra', 'novembra', 'decembra'];
const DAYS_SHORT = ['ned', 'pon', 'uto', 'sre', 'čet', 'pet', 'sub'];
/** Calendar columns, Monday first. */
export const WEEK_HEAD = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];

/** Reads and checks the connector's config; undefined when the page has none. */
export function readDeliveryConfig(raw: unknown): DeliveryConfig | undefined {
  const r = raw as Partial<DeliveryConfig> | null | undefined;
  if (!r || typeof r !== 'object') return undefined;
  const iso = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
  return {
    timeZone: typeof r.timeZone === 'string' && r.timeZone ? r.timeZone : 'Europe/Belgrade',
    weekdays: Array.isArray(r.weekdays) ? r.weekdays.map(Number).filter((n) => n >= 0 && n <= 6) : [],
    dates: Array.isArray(r.dates) ? r.dates.filter(iso) : [],
    ranges: Array.isArray(r.ranges) ? r.ranges.filter((p): p is [string, string] => Array.isArray(p) && iso(p[0]) && iso(p[1])) : [],
    yearly: Array.isArray(r.yearly) ? r.yearly.filter((v): v is string => typeof v === 'string' && /^\d{2}-\d{2}$/.test(v)) : [],
    minDays: Number.isFinite(Number(r.minDays)) ? Math.max(0, Number(r.minDays)) : 1,
    cutoff: typeof r.cutoff === 'string' && /^\d{2}:\d{2}$/.test(r.cutoff) ? r.cutoff : '',
    maxDays: Number.isFinite(Number(r.maxDays)) ? Math.max(0, Number(r.maxDays)) : 0,
    required: r.required === true,
    ...(Number(r.productId) > 0 ? { productId: Number(r.productId) } : {}),
    ...(r.eta === true ? { eta: true } : {}),
    ...(typeof r.ajaxUrl === 'string' && r.ajaxUrl ? { ajaxUrl: r.ajaxUrl } : {}),
  };
}

// ── Days as ISO strings (no time zones past "today") ───────────────────────

/** Today's ISO day and the time "HH:MM" in the shop's time zone. */
export function shopNow(now: Date, timeZone: string): { day: string; time: string } {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return { day: `${get('year')}-${get('month')}-${get('day')}`, time: `${get('hour')}:${get('minute')}` };
  } catch {
    const pad = (n: number) => String(n).padStart(2, '0');
    return { day: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`, time: `${pad(now.getHours())}:${pad(now.getMinutes())}` };
  }
}

function toUtc(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d));
}

function fromUtc(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function addDays(iso: string, days: number): string {
  const d = toUtc(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return fromUtc(d);
}

export function weekday(iso: string): number {
  return toUtc(iso).getUTCDay();
}

/** Why a day cannot be picked, bounds aside: 'weekday', 'date' or ''. */
export function blockedReason(iso: string, rules: DeliveryRules): '' | 'weekday' | 'date' {
  if (rules.weekdays.includes(weekday(iso))) return 'weekday';
  if (rules.dates.includes(iso) || rules.yearly.includes(iso.slice(5))) return 'date';
  if (rules.ranges.some(([from, to]) => from <= iso && iso <= to)) return 'date';
  return '';
}

export function earliestDay(rules: DeliveryRules, now: Date): string {
  const { day, time } = shopNow(now, rules.timeZone);
  const late = rules.cutoff !== '' && time >= rules.cutoff;
  return addDays(day, rules.minDays + (late ? 1 : 0));
}

export function latestDay(rules: DeliveryRules, now: Date): string | undefined {
  return rules.maxDays > 0 ? addDays(shopNow(now, rules.timeZone).day, rules.maxDays) : undefined;
}

export function selectable(iso: string, rules: DeliveryRules, now: Date): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  if (iso < earliestDay(rules, now)) return false;
  const latest = latestDay(rules, now);
  if (latest && iso > latest) return false;
  return blockedReason(iso, rules) === '';
}

/** The first day that can be picked on or after `from` (default: the earliest), within 400 days. */
export function firstSelectable(rules: DeliveryRules, now: Date, from?: string): string | undefined {
  const earliest = earliestDay(rules, now);
  let day = from && from > earliest ? from : earliest;
  const latest = latestDay(rules, now);
  for (let i = 0; i < 400; i++) {
    if (latest && day > latest) return undefined;
    if (blockedReason(day, rules) === '') return day;
    day = addDays(day, 1);
  }
  return undefined;
}

/** Why a picked value cannot go to the cart, or '' when it can ('' = no date). */
export function deliveryError(value: string, rules: DeliveryRules, now: Date): string {
  if (!value) return rules.required ? DELIVERY_TEXTS.required : '';
  return selectable(value, rules, now) ? '' : DELIVERY_TEXTS.invalid;
}

export interface CalendarDay {
  iso: string;
  day: number;
  /** In the month shown (else a filler of the week). */
  inMonth: boolean;
  selectable: boolean;
  today: boolean;
}

/** A month as weeks of seven days, Monday first. `month` is 1–12. */
export function monthGrid(year: number, month: number, rules: DeliveryRules, now: Date): CalendarDay[][] {
  const first = `${year}-${String(month).padStart(2, '0')}-01`;
  const offset = (weekday(first) + 6) % 7;
  const today = shopNow(now, rules.timeZone).day;
  let day = addDays(first, -offset);
  const weeks: CalendarDay[][] = [];
  for (let w = 0; w < 6; w++) {
    const week: CalendarDay[] = [];
    for (let d = 0; d < 7; d++) {
      week.push({
        iso: day,
        day: Number(day.slice(8)),
        inMonth: Number(day.slice(5, 7)) === month,
        selectable: selectable(day, rules, now),
        today: day === today,
      });
      day = addDays(day, 1);
    }
    if (w >= 4 && !week.some((c) => c.inMonth)) break;
    weeks.push(week);
  }
  return weeks;
}

export function monthTitle(year: number, month: number): string {
  const name = MONTHS[month - 1] ?? '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${year}.`;
}

/** "12. novembra" */
export function dayGenitive(iso: string): string {
  return `${Number(iso.slice(8))}. ${MONTHS_GEN[Number(iso.slice(5, 7)) - 1] ?? ''}`;
}

/** "pon 3.11." */
export function dayShort(iso: string): string {
  return `${DAYS_SHORT[weekday(iso)]} ${Number(iso.slice(8))}.${Number(iso.slice(5, 7))}.`;
}

/** "3. ili 4. novembra", "3–6. novembra", "30. novembra – 2. decembra", "12. novembra". */
export function rangeGenitive(from: string, to: string): string {
  if (from === to) return dayGenitive(from);
  if (from.slice(0, 7) === to.slice(0, 7)) {
    const a = Number(from.slice(8));
    const b = Number(to.slice(8));
    const month = MONTHS_GEN[Number(from.slice(5, 7)) - 1] ?? '';
    return b - a === 1 ? `${a}. ili ${b}. ${month}` : `${a}–${b}. ${month}`;
  }
  return `${dayGenitive(from)} – ${dayGenitive(to)}`;
}

// ── The ETA plugin ─────────────────────────────────────────────────────────

export interface EtaData {
  ship_date?: string;
  delivery_from?: string;
  delivery_to?: string;
  target_date?: string;
  risk_level?: 'ok' | 'tight' | 'cannot_guarantee' | null;
}

/** info = the plain estimate; ok / tight / no = a picked date (the plugin's wapf-ok / -tight / -cannot). */
export type EtaTone = 'info' | 'ok' | 'tight' | 'no';

export interface EtaStep {
  icon: string;
  /** The day, as the plugin prints it ("02. Novembar"); shown in capitals. */
  day: string;
  /** A day shown struck through before `day` (a date that cannot be met). */
  struck?: string;
  /** Poručeno / Poslato / Stiže. */
  status: string;
  /** The plugin's step states: the order is in (orange), shipping is next (blue). */
  state: 'completed' | 'active' | '';
}

export interface EtaView {
  tone: EtaTone;
  /** "🚛 Stiže 03. ili 04. Novembra ako poručiš danas!" */
  header: string;
  steps: EtaStep[];
  /** The plugin's line under a picked date's timeline. */
  footer?: string;
  /** A safe day to offer instead (when the picked one cannot be met). */
  suggest?: string;
}

// The ETA plugin's own wording (class-renderer.php), so the card reads the
// same here as the block it replaces: two-digit days, capitalised months.
const PLUGIN_MONTHS = ['Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun', 'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'];
const PLUGIN_MONTHS_GEN = ['Januara', 'Februara', 'Marta', 'Aprila', 'Maja', 'Juna', 'Jula', 'Avgusta', 'Septembra', 'Oktobra', 'Novembra', 'Decembra'];
const dd = (iso: string): string => iso.slice(8, 10);
const month = (iso: string, list: string[]): string => list[Number(iso.slice(5, 7)) - 1] ?? '';

/** "02. Novembar" (format_serbian_date) */
export function pluginDate(iso: string): string {
  return `${dd(iso)}. ${month(iso, PLUGIN_MONTHS)}`;
}

/** "02. Novembra" (format_serbian_date_genitive) */
export function pluginDateGen(iso: string): string {
  return `${dd(iso)}. ${month(iso, PLUGIN_MONTHS_GEN)}`;
}

/** "03. - 04. Novembar" (format_serbian_date_range) */
export function pluginRange(from: string, to: string): string {
  return `${dd(from)}. - ${dd(to)}. ${month(to, PLUGIN_MONTHS)}`;
}

/** "Stiže 03. ili 04. Novembra ako poručiš danas!" (render_timeline_eta header) */
export function pluginHeadline(from: string, to: string): string {
  if (from.slice(5) === to.slice(5)) return `Stiže ${pluginDateGen(from)} ako poručiš danas!`;
  if (from.slice(5, 7) === to.slice(5, 7)) return `Stiže ${dd(from)}. ili ${dd(to)}. ${month(to, PLUGIN_MONTHS_GEN)} ako poručiš danas!`;
  return `Stiže ${pluginDateGen(from)} - ${pluginDateGen(to)} ako poručiš danas!`;
}

const isIso = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);

/**
 * The card, as the ETA plugin draws its timeline: the headline, then
 * Poručeno (today) → Poslato (the ship day) → Stiže. For a picked date the
 * plugin's verdict colours it and words the last step and the footer.
 * `standard` is the plain estimate, whose first day is the earliest delivery.
 */
export function etaView(
  data: EtaData | undefined,
  options: { rules?: DeliveryRules; now?: Date; standard?: EtaData } = {},
): EtaView | undefined {
  if (!data || !isIso(data.delivery_from) || !isIso(data.delivery_to)) return undefined;
  const now = options.now ?? new Date();
  const today = shopNow(now, options.rules?.timeZone ?? 'Europe/Belgrade').day;
  const ship = isIso(data.ship_date) ? data.ship_date : addDays(today, 1);
  const steps: EtaStep[] = [
    { icon: '🛒', day: pluginDate(today), status: DELIVERY_TEXTS.ordered, state: 'completed' },
    { icon: '📦', day: pluginDate(ship), status: DELIVERY_TEXTS.shipped, state: ship < today ? 'completed' : 'active' },
  ];
  const target = isIso(data.target_date) ? data.target_date : undefined;
  if (!target || !data.risk_level) {
    const day = data.delivery_from.slice(5) === data.delivery_to.slice(5) ? pluginDate(data.delivery_from) : pluginRange(data.delivery_from, data.delivery_to);
    steps.push({ icon: '🎁', day, status: DELIVERY_TEXTS.arrives, state: '' });
    return { tone: 'info', header: `🚛 ${pluginHeadline(data.delivery_from, data.delivery_to)}`, steps };
  }
  if (data.risk_level === 'cannot_guarantee') {
    const standard = options.standard;
    const earliest = standard && isIso(standard.delivery_from) ? standard.delivery_from : data.delivery_from;
    // A day that can actually be picked, on or after the earliest delivery.
    const suggest = options.rules ? firstSelectable(options.rules, now, earliest) : undefined;
    steps.push({ icon: '🎁', struck: pluginDate(target), day: pluginDate(earliest), status: DELIVERY_TEXTS.arrives, state: '' });
    return {
      tone: 'no',
      header: `🚫 Dostava ${pluginDateGen(target)} - nemoguće`,
      steps,
      footer: `Molimo odaberite drugi datum. Najraniji datum isporuke: ${pluginDateGen(earliest)}`,
      ...(suggest ? { suggest } : {}),
    };
  }
  if (data.risk_level === 'tight') {
    const after = options.rules ? firstSelectable({ ...options.rules, maxDays: 0 }, now, addDays(target, 1)) : addDays(target, 1);
    steps.push({ icon: '🎁', day: `${pluginDate(target)} (${pluginDate(after ?? addDays(target, 1))})`, status: DELIVERY_TEXTS.arrives, state: '' });
    return {
      tone: 'tight',
      header: `⚠️ Dostava ${pluginDateGen(target)} - rizično`,
      steps,
      footer: `Paket će biti spreman za slanje ${pluginDateGen(ship)}, ali postoji rizik kašnjenja od 1 radnog dana.`,
    };
  }
  const day = data.delivery_from < target ? `${pluginDate(data.delivery_from)} - ${pluginDate(target)}` : pluginDate(target);
  steps.push({ icon: '🎁', day, status: DELIVERY_TEXTS.arrives, state: '' });
  return {
    tone: 'ok',
    header: `✅ Dostava ${pluginDateGen(target)} - izvodljivo`,
    steps,
    footer: 'Ukoliko ne želite da postoji mogućnost da paket stigne dan ranije napomenite nam.',
  };
}

type Fetch = (input: string, init: RequestInit) => Promise<Response>;

/** Asks the ETA plugin; undefined when it cannot answer (the card then hides). */
export async function fetchEta(
  config: Pick<DeliveryConfig, 'ajaxUrl' | 'eta'>,
  productId: number,
  date: string,
  fetchImpl: Fetch = (input, init) => fetch(input, init),
): Promise<EtaData | undefined> {
  if (!config.eta || !config.ajaxUrl || !(productId > 0)) return undefined;
  const body = new URLSearchParams();
  if (date) {
    body.set('action', 'wc_eta_rs_update_product_wapf');
    body.set('product_id', String(productId));
    body.set('requested_date', date);
  } else {
    body.set('action', 'wc_eta_rs_update_product');
    body.set('variation_id', String(productId));
  }
  try {
    const response = await fetchImpl(config.ajaxUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString(),
    });
    if (!response.ok) return undefined;
    const json = (await response.json()) as { success?: boolean; data?: { eta_data?: EtaData } };
    return json?.success && json.data?.eta_data ? json.data.eta_data : undefined;
  } catch {
    return undefined;
  }
}
